/**
 * Role-based authorization utilities
 * 
 * Role hierarchy:
 * - PLATFORM_ADMIN: Super admin, manages all tenants (bypasses tenant filtering)
 * - ADMIN: Tenant admin, can do everything within their tenant
 * - TRADER: Can Execute, Draft, Portfolio Construction, Submit for Pre-Clear
 * - PORTFOLIO_MANAGER: Can Send, Execute, Draft, Portfolio Rebalancing, Portfolio Construction, Submit for Pre-Clear, Override Pre-Clear
 * - REFERENCE_DATA_MANAGER: Owns Security Master / reference data maintenance
 * - COMPLIANCE_OFFICER: Manage compliance rules, IMA documents, Approve/Reject Pre-Clear
 * - MIDDLE_OFFICE_ADMIN: Run reconciliation, Process corporate actions, Generate regulatory reports
 * - RISK_MANAGER: View risk metrics, compliance rules, regulatory reports (read-only)
 * - OPERATIONS_ANALYST: Support middle office tasks, upload files, resolve breaks
 * - RESEARCH_ANALYST: Research and analysis, create draft trades (requires approval)
 * - CLIENT_SERVICE_REP: View assigned portfolios only, client-facing role
 * - USER: Read-only access with restricted sensitive data visibility
 */

export type UserRole = 'PLATFORM_ADMIN' | 'ADMIN' | 'TRADER' | 'PORTFOLIO_MANAGER' |
                       'REFERENCE_DATA_MANAGER' | 'COMPLIANCE_OFFICER' | 'MIDDLE_OFFICE_ADMIN' | 'RISK_MANAGER' |
                       'OPERATIONS_ANALYST' | 'RESEARCH_ANALYST' | 'CLIENT_SERVICE_REP' | 'USER';

export const hasRole = (userRole: string | undefined, requiredRole: UserRole): boolean => {
  if (!userRole) return false;
  
  // PLATFORM_ADMIN can do everything across all tenants
  if (userRole === 'PLATFORM_ADMIN') return true;
  
  // ADMIN can do everything within their tenant
  if (userRole === 'ADMIN') return true;
  
  // Exact match
  if (userRole === requiredRole) return true;
  
  return false;
};

export const hasAnyRole = (userRole: string | undefined, requiredRoles: UserRole[]): boolean => {
  if (!userRole) return false;
  
  // PLATFORM_ADMIN can do everything
  if (userRole === 'PLATFORM_ADMIN') return true;
  
  // ADMIN can do everything within their tenant
  if (userRole === 'ADMIN') return true;
  
  return requiredRoles.includes(userRole as UserRole);
};

// Platform admin check (cross-tenant capabilities)
export const isPlatformAdmin = (userRole: string | undefined): boolean => {
  return userRole === 'PLATFORM_ADMIN';
};

// Specific permission checks
export const canDraft = (userRole: string | undefined): boolean => {
  return hasAnyRole(userRole, ['ADMIN', 'TRADER', 'PORTFOLIO_MANAGER']);
};

export const canSend = (userRole: string | undefined): boolean => {
  return hasAnyRole(userRole, ['ADMIN', 'PORTFOLIO_MANAGER']);
};

export const canExecute = (userRole: string | undefined): boolean => {
  return hasAnyRole(userRole, ['ADMIN', 'TRADER', 'PORTFOLIO_MANAGER']);
};

export const canAccessPortfolioConstruction = (userRole: string | undefined): boolean => {
  return hasAnyRole(userRole, ['ADMIN', 'TRADER', 'PORTFOLIO_MANAGER']);
};

export const canAccessPortfolioRebalancing = (userRole: string | undefined): boolean => {
  return hasAnyRole(userRole, ['ADMIN', 'PORTFOLIO_MANAGER']);
};

export const canAccessPortfolioHedging = (userRole: string | undefined): boolean => {
  return hasAnyRole(userRole, ['ADMIN', 'TRADER', 'PORTFOLIO_MANAGER']);
};

export const canEdit = (userRole: string | undefined): boolean => {
  // Only ADMIN, TRADER, and PORTFOLIO_MANAGER can edit
  // Regular USER is read-only
  return hasAnyRole(userRole, ['ADMIN', 'TRADER', 'PORTFOLIO_MANAGER']);
};

export const canEditComplianceRules = (userRole: string | undefined): boolean => {
  return hasAnyRole(userRole, ['ADMIN', 'COMPLIANCE_OFFICER']);
};

export const canManageSecurityMaster = (userRole: string | undefined): boolean => {
  return hasAnyRole(userRole, ['ADMIN', 'REFERENCE_DATA_MANAGER', 'MIDDLE_OFFICE_ADMIN', 'OPERATIONS_ANALYST']);
};

export const canLoadIMA = (userRole: string | undefined): boolean => {
  return hasAnyRole(userRole, ['ADMIN', 'COMPLIANCE_OFFICER']);
};

export const canRunReconciliation = (userRole: string | undefined): boolean => {
  return hasAnyRole(userRole, ['ADMIN', 'MIDDLE_OFFICE_ADMIN']);
};

export const canProcessCorporateActions = (userRole: string | undefined): boolean => {
  return hasAnyRole(userRole, ['ADMIN', 'MIDDLE_OFFICE_ADMIN']);
};

export const canGenerateRegulatoryReports = (userRole: string | undefined): boolean => {
  return hasAnyRole(userRole, ['ADMIN', 'MIDDLE_OFFICE_ADMIN']);
};

export const canViewSensitiveData = (userRole: string | undefined): boolean => {
  return hasAnyRole(userRole, ['ADMIN', 'REFERENCE_DATA_MANAGER', 'COMPLIANCE_OFFICER', 'MIDDLE_OFFICE_ADMIN', 'RISK_MANAGER']);
};

export const canApproveRejectTrades = (userRole: string | undefined): boolean => {
  return hasAnyRole(userRole, ['ADMIN', 'COMPLIANCE_OFFICER']);
};

export const canSubmitForPreClear = (userRole: string | undefined): boolean => {
  return hasAnyRole(userRole, ['ADMIN', 'TRADER', 'PORTFOLIO_MANAGER']);
};

export const canOverridePreClear = (userRole: string | undefined): boolean => {
  return hasAnyRole(userRole, ['ADMIN', 'PORTFOLIO_MANAGER', 'COMPLIANCE_OFFICER']);
};

export const canViewPortfolio = (userRole: string | undefined, portfolioId: number, userRestrictions: number[] | null): boolean => {
  if (hasAnyRole(userRole, ['ADMIN', 'PLATFORM_ADMIN'])) return true;
  if (userRole === 'CLIENT_SERVICE_REP' && userRestrictions) {
    return userRestrictions.includes(portfolioId);
  }
  return hasAnyRole(userRole, ['TRADER', 'PORTFOLIO_MANAGER', 'REFERENCE_DATA_MANAGER', 'COMPLIANCE_OFFICER', 'MIDDLE_OFFICE_ADMIN', 'RISK_MANAGER', 'RESEARCH_ANALYST', 'OPERATIONS_ANALYST']);
};
